/** @jsxImportSource @emotion/react */
import { memo } from "react";
import { useFormContext } from "react-hook-form";

import { getProhibitedCharacters } from "utilities/getProhibitedCharacters.js";

import ValidationErrorText from "components/ui/validation/ValidationErrorText";

// CSS読み込み
import {
  halfWrap,
  harfEmpty,
  inputWrap,
  inputArea_text,
  inputArea_text_error,
} from "assets/css/stylesCss";

// validation読み込み
import {
  validMaxSizeInPairs,
} from "utilities/validation";

/**
 * 入力フォーム ハーフ
 * @param {Object} items name、placeholder、maxLength、inputMode、rulesを設定したオブジェクト
 * @param {Object} pairsValid プレースホルダー表示用文字列
 * @param {Boolean} isYubinNo 郵便番号判定値
 * @param {Function} setAddressKj 都道府県・市区町村・丁目設定用関数
 */
const InputTextHalf = memo(
  ({ items, pairsValid, isYubinNo = false, setAddressKj }) => {
    const {
      register,
      formState: { errors },
      getValues,
      setValue,
      clearErrors,
      trigger,
    } = useFormContext();

    const hasMaxSizeInPairsError = (names) => {
      return names.some(
        (name) => errors[name] && errors[name].type === "isValidMaxSizeInPairs"
      );
    };

    return (
      <>
        <div css={halfWrap}>
          {items.map((item) => (
            <div key={item.name} css={inputWrap}>
              <input
                {...register(item.name, {
                  ...item.rules,
                  validate: {
                    // ペアでの桁数チェック
                    isValidMaxSizeInPairs: () => {
                      // pairsValidが指定されていない場合
                      if (!pairsValid || Object.keys(pairsValid).length === 0) {
                        return true;
                      }

                      // 値が存在しない場合、チェックしない
                      if (
                        !getValues(items[0].name) ||
                        !getValues(items[1].name)
                      ) {
                        return true;
                      }

                      if (
                        validMaxSizeInPairs(
                          getValues(items[0].name).length,
                          getValues(items[1].name).length,
                          pairsValid.maxSize
                        )
                      ) {
                        return true;
                      } else {
                        return pairsValid.message;
                      }
                    },

                    // 禁止文字チェック
                    isOnlyAllowedCharacters: (value) =>
                      getProhibitedCharacters(value).length === 0
                        ? true
                        : "利用できない文字が含まれています。",
                  },
                  onBlur: () => {
                    // ボタン活性
                    setValue("isActive", true, { shouldValidate: true });

                    // 前後のスペースを削除し設定
                    setValue(item.name, getValues(item.name).trim());

                    // ペアのエラーが存在する場合
                    if (
                      pairsValid &&
                      hasMaxSizeInPairsError([items[0].name, items[1].name])
                    ) {
                      clearErrors([items[0].name, items[1].name]);
                    }

                    // 値が存在する場合
                    if (getValues(item.name)) {
                      if (pairsValid) {
                        // pairsValidが指定されている場合
                        // ペアになっている項目を検証する
                        trigger(items[0].name);
                        trigger(items[1].name);
                      } else {
                        setValue(item.name, getValues(item.name), {
                          shouldValidate: true,
                        });
                      }
                    }

                  },
                  onChange: () => {

                    // 初期化
                    clearErrors(item.name);

                    // 値が存在しない場合
                    if (!getValues(item.name)) {
                      // ボタン活性
                      setValue("isActive", true);
                    } else {
                      // ボタン非活性
                      setValue("isActive", false);
                    }

                    if (isYubinNo) {
                      // 都道府県・市区町村・丁目設定
                      setAddressKj();

                      // 住所情報
                      setValue("addressKj", "")
                    }
                  }
                })}
                css={[
                  errors[item.name] ? inputArea_text_error : inputArea_text
                ]}
                placeholder={item.placeholder}
                maxLength={item.maxLength ? item.maxLength : ""}
                inputMode={item.inputMode ? item.inputMode : ""}
                onKeyDown={e => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
            </div>
          ))}

          {/* 空領域 **/}
          {items.length === 1 ? <div css={harfEmpty} /> : null}

        </div>

        {/* isValidMaxSizeInPairsのエラーはhalfWrapしない **/}
        {pairsValid &&
          ((errors[items[0].name] &&
            errors[items[0].name].type === "isValidMaxSizeInPairs") ||
            (errors[items[1].name] &&
              errors[items[1].name].type === "isValidMaxSizeInPairs")) && (
            <ValidationErrorText>{pairsValid.message}</ValidationErrorText>
          )}

        <div css={halfWrap}>
          {items.map((item) => {
            return (
              <div key={item.name} css={inputWrap}>
                {/* エラーメッセージ **/}
                {errors[item.name] &&
                  errors[item.name].type !== "isValidMaxSizeInPairs" && (
                    <ValidationErrorText>
                      {errors[item.name].message}
                    </ValidationErrorText>
                  )}
              </div>
            );
          })}
        </div>
      </>
    );
  }
);

export default InputTextHalf;
